/**
 * cacheBus — invalidación cross-tab de useCachedFetch.
 *
 * Cuando un técnico captura una parcela en una pestaña, otras pestañas
 * abiertas (otra ventana, otra app del mismo dominio) deben re-fetch los
 * hooks afectados sin esperar a que expire el TTL de sessionStorage.
 *
 * - `invalidate(keys)`: borra entries del sessionStorage cache + broadcast
 *   a otras pestañas. La pestaña actual también recibe el evento (vía el
 *   listener), así que un solo call invalida todo.
 * - `subscribe(handler)`: registra listener. Devuelve unsubscriber.
 * - `getChannel()`: acceso al BroadcastChannel singleton para casos donde
 *   se necesite enviar custom messages.
 *
 * Naming: las `keys` corresponden 1:1 con la `key` que se pasa a
 * useCachedFetch (sin el prefix `aosenuma-cache:`). Ejemplos:
 *   'parcelas_tecnicas', 'inventario', 'kpi_dashboard',
 *   'expedientes_list', 'parcelas_publicas'.
 *
 * Fallback: si BroadcastChannel no está disponible (Safari muy viejo,
 * iframe sandbox), usamos `storage` event de localStorage como puente —
 * escribimos un timestamp en una key específica, otras pestañas leen
 * el cambio.
 */

const CACHE_PREFIX = 'aosenuma-cache:';
const CHANNEL_NAME = 'aosenuma-cache-bus';
const STORAGE_FALLBACK_KEY = 'aosenuma-cache-bus:fallback';

export interface CacheInvalidateMessage {
  type: 'invalidate';
  keys: string[];
  /** Timestamp del emisor — útil para evitar loops si la misma tab recibe su propio mensaje. */
  ts: number;
}

let channelSingleton: BroadcastChannel | null = null;
function getChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined') return null;
  if (typeof BroadcastChannel === 'undefined') return null;
  if (channelSingleton) return channelSingleton;
  try {
    channelSingleton = new BroadcastChannel(CHANNEL_NAME);
  } catch {
    channelSingleton = null;
  }
  return channelSingleton;
}

const listeners = new Set<(msg: CacheInvalidateMessage) => void>();

function deliverToListeners(msg: CacheInvalidateMessage): void {
  for (const l of listeners) {
    try { l(msg); } catch { /* listener throw — no debe romper el bus */ }
  }
}

// Registrar listener al canal una sola vez. La misma pestaña que emite NO
// recibe su propio mensaje (BroadcastChannel filter). Por eso `invalidate`
// llama a deliverToListeners directamente además de postMessage.
let channelWired = false;
function ensureChannelWired(): void {
  if (channelWired) return;
  const ch = getChannel();
  if (ch) {
    ch.addEventListener('message', (e) => {
      const msg = e.data as CacheInvalidateMessage | undefined;
      if (msg && msg.type === 'invalidate' && Array.isArray(msg.keys)) {
        // Borrar el cache de sessionStorage en esta pestaña también
        // (la pestaña que emitió el mensaje ya lo borró antes de postMessage).
        clearSessionCache(msg.keys);
        deliverToListeners(msg);
      }
    });
  } else if (typeof window !== 'undefined') {
    // Fallback storage event para browsers sin BroadcastChannel
    window.addEventListener('storage', (e) => {
      if (e.key !== STORAGE_FALLBACK_KEY || !e.newValue) return;
      try {
        const msg = JSON.parse(e.newValue) as CacheInvalidateMessage;
        if (msg.type === 'invalidate' && Array.isArray(msg.keys)) {
          clearSessionCache(msg.keys);
          deliverToListeners(msg);
        }
      } catch { /* JSON corrupto — ignorar */ }
    });
  }
  channelWired = true;
}

function clearSessionCache(keys: string[]): void {
  if (typeof window === 'undefined') return;
  try {
    for (const k of keys) {
      window.sessionStorage.removeItem(CACHE_PREFIX + k);
    }
  } catch { /* QuotaExceeded u otro */ }
}

/**
 * Invalidar caches localmente + broadcast a otras pestañas. Llamar tras
 * mutaciones (POST /v1/captura, INSERT visitas, /v1/upload/complete OK)
 * para que tanto esta pestaña como las del cliente Elawan se refresquen.
 *
 * Importante: el broadcast NO espera respuesta. Si la otra pestaña está
 * suspendida (Safari Memory Saver), recibirá el mensaje al volver a la
 * fila de eventos al activarse.
 */
export function invalidateCache(keys: string[]): void {
  if (typeof window === 'undefined' || keys.length === 0) return;
  ensureChannelWired();
  // 1) Limpiar el cache local PRIMERO. Si re-render ocurre antes del
  //    broadcast, el hook ve sessionStorage limpio y refetch.
  clearSessionCache(keys);
  const msg: CacheInvalidateMessage = { type: 'invalidate', keys, ts: Date.now() };
  // 2) Notificar a listeners de ESTA pestaña (BroadcastChannel no entrega
  //    a la misma tab que emite).
  deliverToListeners(msg);
  // 3) Broadcast al resto.
  const ch = getChannel();
  if (ch) {
    try { ch.postMessage(msg); } catch { /* canal cerrado */ }
  } else {
    // Fallback storage event: setear y limpiar (el evento solo dispara
    // entre tabs, no en esta misma).
    try {
      window.localStorage.setItem(STORAGE_FALLBACK_KEY, JSON.stringify(msg));
      // Borrar después de un tick para que el siguiente invalidate
      // dispare un storage event aunque keys sean idénticas.
      window.setTimeout(() => {
        try { window.localStorage.removeItem(STORAGE_FALLBACK_KEY); } catch { /* ignore */ }
      }, 50);
    } catch { /* storage lleno o disabled */ }
  }
}

/**
 * Suscribirse a invalidaciones (locales + remotas). Usado por useCachedFetch
 * para refetch automático al recibir invalidate de cualquier pestaña.
 */
export function subscribeCache(handler: (msg: CacheInvalidateMessage) => void): () => void {
  ensureChannelWired();
  listeners.add(handler);
  return () => { listeners.delete(handler); };
}
